import React, { useEffect, useMemo } from 'react';

import { DataGrid } from '@placer-ui/components';
import styles from './support-request-results.module.scss';
import {
    RequestResultsStatus,
    SupportRequestResultsModalTableRow,
} from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { useTableConfig } from './use-table-config';
import {
    ORIGINAL_DATA_PREFIX,
    sumItemsByStatus,
} from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/utils';
import { TopFiltersSection } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/top-filter-section/top-filters-section';
import { BannerSection } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/banner-section/banner-section';
import { getAllUniqueDataKeys } from 'features/my-zone/support-requests/components/support-requests-modals/utils/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
    isSubmittingTableRequestsStore,
    mappedFileDataStore,
    resultsTableEditedStore,
    resultsTableFiltersStore,
    statusMapStore,
    supportResultsLoadingRowsStore,
    tempDataTableStore,
} from 'features/my-zone/support-requests/store/property-store';
import { useSupportResultsTableData } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/use-support-results-table-data';
import { resetChallengeData } from './components/ask-for-review/utils';

export const SupportRequestsResultsModal = () => {
    const [filters, setFilters] = useAtom(resultsTableFiltersStore);
    const isSubmittingTableRequests = useAtomValue(isSubmittingTableRequestsStore);
    const setIsDataEdited = useSetAtom(resultsTableEditedStore);
    const [mappedFileData, setMappedFileData] = useAtom(mappedFileDataStore);
    const supportResultsLoadingRows = useAtomValue(supportResultsLoadingRowsStore);
    const setStatusMapStore = useSetAtom(statusMapStore);
    const setTempDataTable = useSetAtom(tempDataTableStore);

    const { getColumnConfig } = useTableConfig({
        isSubmitting: isSubmittingTableRequests,
    });

    const { allTableData: dataTable, tableDataByFilter: dataToDisplay } =
        useSupportResultsTableData();

    const columns = useMemo(
        () => getColumnConfig(mappedFileData?.matching),
        [getColumnConfig, mappedFileData],
    );

    const { statusMap, statusList } = useMemo(() => sumItemsByStatus(dataTable), [dataTable]);

    useEffect(() => {
        setStatusMapStore(statusMap);
    }, [setStatusMapStore, statusMap]);

    //TODO PLAC-63275 remove after refactor
    useEffect(() => {
        setTempDataTable(dataTable);
    }, [dataTable, setTempDataTable]);

    useEffect(() => {
        setFilters((prev) => {
            return statusList.reduce((acc, { label }) => {
                return {
                    ...acc,
                    [label]: prev?.[label] ?? false,
                };
            }, {});
        });
    }, [setFilters, statusList]);

    const handleFilterChange = (filter: RequestResultsStatus) => {
        setFilters((prev) => {
            return {
                ...prev,
                [filter]: !prev[filter],
            };
        });
    };

    const handleCellItemEdited = (row: any) => {
        setIsDataEdited(true);

        const existingKeys = getAllUniqueDataKeys(mappedFileData?.data);

        const rowData = Object.entries(row.data).reduce((acc, [key, value]) => {
            if (existingKeys.includes(key) || key.startsWith(ORIGINAL_DATA_PREFIX)) {
                return { ...acc, [key]: value };
            }
            return acc;
        }, {} as Omit<SupportRequestResultsModalTableRow, 'actions'>);

        setMappedFileData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                data: prev.data.map((item) =>
                    item.id === rowData.id
                        ? {
                              ...rowData,
                              ...resetChallengeData,
                              isEdited: true,
                          }
                        : item,
                ),
            };
        });
    };

    return (
        <div className={styles.contentWrapper}>
            {statusMap && <BannerSection statusMap={statusMap} />}
            <TopFiltersSection
                filters={filters}
                dataToDisplay={dataToDisplay}
                onFilterChange={handleFilterChange}
                columns={columns}
            />

            <div data-testid="support-request-results-modal-table" className={styles.tableWrapper}>
                <DataGrid<SupportRequestResultsModalTableRow>
                    gridDensity="standard"
                    columns={columns}
                    rowData={dataToDisplay}
                    onCellValueChanged={handleCellItemEdited}
                    getRowId={(params) => params.data.id}
                    loadingRows={supportResultsLoadingRows}
                    overrideNoResult={{
                        filter: {
                            title: 'No Properties Match Your Search',
                            message: 'Try adjusting your filters',
                        },
                    }}
                    features={{
                        checkboxSelection: false,
                        editable: false,
                        filter: true,
                        resizable: true,
                        sortable: true,
                        wrapText: false,
                    }}
                />
            </div>
        </div>
    );
};
